import { ContentService } from './contentService';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  sources?: ChunkSource[];
  timestamp: Date;
}

export interface ChunkSource {
  text: string;
  chunkIndex: number;
  similarity: number;
  metadata?: any;
}

export type LLMProvider = 'openai' | 'anthropic';

export class ChatService {
  private static instance: ChatService;
  private contentService: ContentService;
  private conversationHistory: ChatMessage[] = [];

  private constructor() {
    // Note: OpenAI and Anthropic clients are not initialized here anymore
    // API calls are now handled by secure backend serverless functions
    // No need to expose API keys to the browser

    this.contentService = ContentService.getInstance();

    // Add system prompt
    this.conversationHistory.push({
      role: 'system',
      content: `You are an expert AI tutor specializing in teaching occupational therapists about generative AI and its applications in healthcare. Your role is to:

1. Answer questions based on the provided learning material context
2. Provide clear, practical explanations tailored to OT professionals
3. Use examples relevant to occupational therapy practice
4. Cite sources when referencing specific material
5. Encourage best practices and ethical AI use in healthcare
6. If a question is outside the provided context, acknowledge it and provide general guidance

Keep responses conversational, supportive, and focused on practical application. Use the context chunks provided to give accurate, relevant answers.`,
      timestamp: new Date()
    });
  }

  public static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  /**
   * Send a message and get AI response with RAG
   */
  public async sendMessage(
    userMessage: string,
    provider: LLMProvider = 'anthropic',
    topK: number = 5
  ): Promise<ChatMessage> {
    // Add user message to history
    const userMsg: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    this.conversationHistory.push(userMsg);

    try {
      // Step 1: Search for relevant content using RAG
      const searchResults = await this.contentService.searchContent(userMessage, topK);

      // Step 2: Build context from search results
      const context = this.buildContextFromChunks(searchResults);
      const sources: ChunkSource[] = searchResults.map(result => ({
        text: result.content, // Fixed: vectorStore returns 'content' not 'text'
        chunkIndex: result.chunkIndex,
        similarity: result.similarity,
        metadata: result.metadata
      }));

      // Step 3: Generate response using selected LLM
      const assistantContent = provider === 'anthropic'
        ? await this.generateAnthropicResponse(userMessage, context)
        : await this.generateOpenAIResponse(userMessage, context);

      // Step 4: Create assistant message
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: assistantContent,
        sources: sources.length > 0 ? sources : undefined,
        timestamp: new Date()
      };

      this.conversationHistory.push(assistantMsg);
      return assistantMsg;

    } catch (error) {
      console.error('Error generating response:', error);
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error generating a response. Please try again.',
        timestamp: new Date()
      };
      this.conversationHistory.push(errorMsg);
      return errorMsg;
    }
  }

  /**
   * Build context string from search results
   */
  private buildContextFromChunks(chunks: any[]): string {
    if (chunks.length === 0) {
      return 'No relevant context found in the learning materials.';
    }

    const contextParts = chunks.map((chunk, index) => {
      return `[Source ${index + 1}] (Relevance: ${(chunk.similarity * 100).toFixed(1)}%)
${chunk.content}`; // Fixed: use 'content' instead of 'text'
    });

    return `Here are the most relevant excerpts from the learning materials:

${contextParts.join('\n\n---\n\n')}`;
  }

  /**
   * Generate response using Anthropic Claude (via secure API endpoint)
   */
  private async generateAnthropicResponse(question: string, context: string): Promise<string> {
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: question,
        provider: 'anthropic',
        context: context,
        systemPrompt: this.conversationHistory[0].content
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || 'Failed to get response from API');
    }

    const data = await response.json();
    return data.content;
  }

  /**
   * Generate response using OpenAI GPT (via secure API endpoint)
   */
  private async generateOpenAIResponse(question: string, context: string): Promise<string> {
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: question,
        provider: 'openai',
        context: context,
        systemPrompt: this.conversationHistory[0].content
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || 'Failed to get response from API');
    }

    const data = await response.json();
    return data.content;
  }

  /**
   * Get conversation history
   */
  public getHistory(): ChatMessage[] {
    return this.conversationHistory.filter(msg => msg.role !== 'system');
  }

  /**
   * Clear conversation history
   */
  public clearHistory(): void {
    const systemMessage = this.conversationHistory[0];
    this.conversationHistory = [systemMessage];
  }

  /**
   * Get statistics
   */
  public getStats() {
    return {
      totalMessages: this.conversationHistory.length - 1, // Exclude system message
      userMessages: this.conversationHistory.filter(m => m.role === 'user').length,
      assistantMessages: this.conversationHistory.filter(m => m.role === 'assistant').length
    };
  }
}
